import {
  Currency,
  LenderName,
  PaymentMethodModificationPreview,
  PaymentMethodModificationRequest,
} from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { AutomobileFormItem } from '@/components/modules/Application/components/Cart/DefaultCart/components/ArticleTypes/Automobile/types/AutomobileFormItem';
import { DialogStepContent } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/components/DialogStepContent';
import { EditAutoPaymentMethodConditionsSummary } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodConditionsSummary';
import { EditAutoPaymentMethodSpecsChangesReview } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/components/EditAutoPaymentMethodSpecsChangesReview';
import { createSpecsLiveSummaryRows } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodLiveSummaryRows.map';
import {
  createEditAutoPaymentMethodPreviewConditions,
  EditAutoPaymentMethodPreviewConditionsData,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/maps/editAutoPaymentMethodPreviewConditions.map';
import { EditAutoPaymentMethodFinancingFields } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/schemas/editAutoPaymentMethodForm.schema';
import { EditAutoPaymentMethodEntryMode } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/EditAutoPaymentMethodEntryMode';
import { FinancingLiveSummaryData } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/components/EditAutoPaymentMethodDialog/types/FinancingLiveSummaryData';
import { hasModificationPayload } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Cart/Auto/maps/vehicleModificationPreview.map';
import { CustomAlert } from '@/components/shared/components/CustomAlert';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { isDeepEqual } from '@/utils/isObjectDeepEqual';

interface EditAutoPaymentMethodChangesReviewProps {
  entryMode: EditAutoPaymentMethodEntryMode;
  currency: Currency;
  initialFormItem: AutomobileFormItem;
  reviewItem: AutomobileFormItem;
  initialFinancing: FinancingLiveSummaryData;
  reviewFinancing: EditAutoPaymentMethodFinancingFields;
  modificationBody: PaymentMethodModificationRequest;
  paymentMethodModificationPreview?: PaymentMethodModificationPreview;
  fallbackApr?: number | null;
  selectedLenderName?: LenderName;
  onNext: () => void;
  onPrev: () => void;
  loading: boolean;
  showRequestDelayWarning: boolean;
}

export const EditAutoPaymentMethodChangesReview = ({
  entryMode,
  currency,
  initialFormItem,
  reviewItem,
  initialFinancing,
  reviewFinancing,
  modificationBody,
  paymentMethodModificationPreview,
  fallbackApr,
  selectedLenderName,
  onNext,
  onPrev,
  loading,
  showRequestDelayWarning,
}: EditAutoPaymentMethodChangesReviewProps) => {
  const { getTextIdTranslation } = useTranslation();

  const isFinancingDirty = !isDeepEqual(initialFinancing, reviewFinancing);
  const hasVehicleAmountChanged = initialFormItem.price !== reviewItem.price;

  const showFinancingReview =
    entryMode === 'financing' || isFinancingDirty || Boolean(modificationBody.paymentMethodOptions);
  const showSpecsReview = entryMode === 'vehicle' || (entryMode === 'financing' && hasVehicleAmountChanged);

  const initialConditions: EditAutoPaymentMethodPreviewConditionsData = useMemo(() => {
    return createEditAutoPaymentMethodPreviewConditions({
      preview: undefined,
      financing: initialFinancing,
      currency,
      fallbackApr,
      getTextIdTranslation,
    });
  }, [initialFinancing, currency, fallbackApr, getTextIdTranslation]);

  const newConditions = useMemo(
    () =>
      createEditAutoPaymentMethodPreviewConditions({
        preview: paymentMethodModificationPreview,
        financing: reviewFinancing,
        currency,
        fallbackApr,
        getTextIdTranslation,
      }),
    [paymentMethodModificationPreview, reviewFinancing, currency, fallbackApr, getTextIdTranslation],
  );

  const changedSpecsRows = useMemo(
    () =>
      createSpecsLiveSummaryRows({
        initialItem: initialFormItem,
        currentItem: reviewItem,
        currency,
      })
        .filter((row) => row.labelTextId !== 'CASH_PRICE')
        .filter((row) => row.hasChanged),
    [initialFormItem, reviewItem, currency],
  );

  const isSameLenderInvalid = paymentMethodModificationPreview?.sameLenderStillValid === false;

  return (
    <DialogStepContent
      titleTextId={'CHANGES_REVIEW'}
      descriptionTextId={'CHANGES_REVIEW_DESCRIPTION'}
      nextButtonTextId={'CONFIRM_CHANGES'}
      onNext={() => {
        onNext();
      }}
      onPrev={onPrev}
      onPrevDisabled={loading}
      loading={loading}
      disabled={!hasModificationPayload(modificationBody) || loading || (showFinancingReview && isSameLenderInvalid)}
    >
      <FlexColumn alignItems={'center'} gridGap={32}>
        {showFinancingReview && (
          <>
            <EditAutoPaymentMethodConditionsSummary
              initialConditions={initialConditions}
              newConditions={newConditions}
              selectedLenderName={selectedLenderName}
              isInvalid={isSameLenderInvalid}
            />
          </>
        )}

        {showSpecsReview && <EditAutoPaymentMethodSpecsChangesReview rows={changedSpecsRows} />}
        {/* For the moment ignore the keys order_would_change and new_top_lender_id */}
        {/* {showFinancingReview && showOrderWouldChangeWarning && (
          <CustomAlert
            severity="warning"
            title="AUTO_MODIFICATION_ORDER_WOULD_CHANGE"
            description={
              <>
                <Text tId="AUTO_MODIFICATION_ORDER_WOULD_CHANGE_DESCRIPTION" />
                {newTopLenderName && (
                  <>
                    {' '}
                    <Text
                      tId="AUTO_MODIFICATION_ORDER_WOULD_CHANGE_OPTIMAL_LENDER"
                      dynamicDataTexts={[getTextIdTranslation('newTopLenderName')]}
                    />
                  </>
                )}
              </>
            }
          />
        )} */}
        {showFinancingReview && isSameLenderInvalid && (
          <CustomAlert
            severity="error"
            title="AUTO_MODIFICATION_SAME_LENDER_INVALID"
            description="AUTO_MODIFICATION_SAME_LENDER_INVALID_DESCRIPTION"
          />
        )}
        {showRequestDelayWarning && (
          <Font className="appear-anim" variant="body2" color="textSecondary">
            <Text tId={'REQUEST_TO_LENDER_DELAY_WARNING'} />
          </Font>
        )}
      </FlexColumn>
    </DialogStepContent>
  );
};
